'use client';

import React, { PropsWithChildren, useEffect, useMemo } from 'react';
import { useCartStore } from 'components/shared/Cart';
import { PerformanceLabsIndiaProvider } from './buy/india/PerformanceLabsIndiaContext';
import PLBottomBar from './buy/india/PLBottomBar';
import { getPerformanceLabCartItemCount } from './buy/india/performanceLabCartUtils';
import { useIsIndiaRegion } from './hooks';


export const PL_CART_BAR_OFFSET_CSS_VAR = '--pl-cart-bar-offset';
const PL_CART_BAR_OFFSET_PX = 100;

const PerformanceLabIndiaCartShellInner: React.FC<
  PropsWithChildren<{ withBottomBar?: boolean }>
> = ({ children, withBottomBar = true }) => {
  const cart = useCartStore((s) => s.cart);
  const plItemCount = useMemo(
    () => getPerformanceLabCartItemCount(cart),
    [cart],
  );

  useEffect(() => {
    const offset =
      withBottomBar && plItemCount > 0 ? `${PL_CART_BAR_OFFSET_PX}px` : '0px';
    document.documentElement.style.setProperty(PL_CART_BAR_OFFSET_CSS_VAR, offset);

    return () => {
      document.documentElement.style.removeProperty(PL_CART_BAR_OFFSET_CSS_VAR);
    };
  }, [plItemCount, withBottomBar]);

  return (
    <>
      {children}
      {withBottomBar && <PLBottomBar $fixed />}
    </>
  );
};

type PerformanceLabIndiaCartShellProps = PropsWithChildren<{
  /**
   * Mount the India cart provider for every region (not just India). The
   * India-only checkout bottom bar still renders only for India; elsewhere the
   * provider simply enables region-aware cards (e.g. the IV Lounge lineup, which
   * falls back to "Notify Me" outside India).
   */
  alwaysProvide?: boolean;
  withBottomBar?: boolean;
}>;

export const PerformanceLabIndiaCartShell: React.FC<
  PerformanceLabIndiaCartShellProps
> = ({ children, alwaysProvide = false, withBottomBar = true }) => {
  const isIndia = useIsIndiaRegion();

  if (!isIndia) {
    if (alwaysProvide) {
      return (
        <PerformanceLabsIndiaProvider>{children}</PerformanceLabsIndiaProvider>
      );
    }
    return <>{children}</>;
  }

  return (
    <PerformanceLabsIndiaProvider>
      <PerformanceLabIndiaCartShellInner withBottomBar={withBottomBar}>
        {children}
      </PerformanceLabIndiaCartShellInner>
    </PerformanceLabsIndiaProvider>
  );
};
